import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";

const F = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const INTERVAL = 6000;   // ms between auto-advances
const FADE_MS = 1100;    // crossfade duration
const SIZE = 340;        // box size, px (matches the old dotwork canvas)

/* Smooth crossfading showcase of the latest releases — real cover art,
   auto-advancing, no manual controls (matches the auto-only behavior of
   the dotwork component it replaces). Two stacked <img> layers swap which
   one is "front" so the outgoing and incoming covers fade simultaneously,
   rather than a hard cut followed by a fade-in. */
export default function LatestReleasesShowcase({ releases }) {
  const items = useMemo(() => (releases || []).filter(r => r.cover).slice(0, 5), [releases]);

  const [idx, setIdx] = useState(0);
  const [frontLayer, setFrontLayer] = useState(0);
  const [layerSrc, setLayerSrc] = useState(() => [items[0]?.cover || "", ""]);

  const idxRef = useRef(0);
  const frontRef = useRef(0);
  const timerRef = useRef(null);
  const rafRef = useRef(null);

  /* preload every cover once so later swaps paint instantly (no flash) */
  useEffect(() => {
    items.forEach(r => { const img = new Image(); img.src = r.cover; });
  }, [items]);

  const advance = useCallback(() => {
    if (items.length <= 1) return;
    const next = (idxRef.current + 1) % items.length;
    const backLayer = frontRef.current === 0 ? 1 : 0;

    setLayerSrc(srcs => {
      const copy = [...srcs];
      copy[backLayer] = items[next].cover;
      return copy;
    });

    idxRef.current = next;
    setIdx(next);

    // Wait two frames so the browser paints the new (still-transparent)
    // layer before flipping its opacity — otherwise the src swap and the
    // opacity change can land in the same paint and the crossfade never
    // actually animates.
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        frontRef.current = backLayer;
        setFrontLayer(backLayer);
      });
    });
  }, [items]);

  useEffect(() => {
    if (items.length <= 1) return;
    timerRef.current = setInterval(advance, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [advance, items.length]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  if (!items.length) return null;
  const current = items[idx];

  return (
    <div style={{
      background: "#000",
      padding: "48px 24px 40px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}>

      {/* label */}
      <p style={{
        fontFamily: F, fontSize: "8px", letterSpacing: "0.4em",
        textTransform: "uppercase", color: "rgba(240,237,232,0.25)",
        marginBottom: "28px",
      }}>
        Featured
      </p>

      {/* crossfading cover box */}
      <div style={{
        position: "relative",
        width: SIZE, height: SIZE,
        maxWidth: "100%",
        overflow: "hidden",
        background: "#0a0a0a",
      }}>
        {[0, 1].map(layer => (
          <img
            key={layer}
            src={layerSrc[layer] || undefined}
            alt=""
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover",
              opacity: layerSrc[layer] && layer === frontLayer ? 1 : 0,
              transition: `opacity ${FADE_MS}ms ease`,
            }}
          />
        ))}
      </div>

      {/* artist + title */}
      <div style={{ textAlign: "center", marginTop: "28px", marginBottom: "24px" }}>
        <p
          key={`artist-${idx}`}
          style={{
            fontFamily: F, fontSize: "9px", letterSpacing: "0.3em",
            textTransform: "uppercase", color: "rgba(240,237,232,0.4)",
            marginBottom: "8px",
            animation: "yen-showcase-fade 0.5s ease both",
          }}
        >
          {current.artist}
        </p>
        <p
          key={`title-${idx}`}
          style={{
            fontFamily: F, fontSize: "24px", fontWeight: 900,
            letterSpacing: "-0.02em", lineHeight: 1.1,
            color: "#f0ede8", textTransform: "uppercase",
            animation: "yen-showcase-fade 0.5s 0.07s ease both",
          }}
        >
          {current.title}
        </p>
        {current.type && (
          <p
            key={`type-${idx}`}
            style={{
              fontFamily: F, fontSize: "8px", letterSpacing: "0.3em",
              textTransform: "uppercase", color: "rgba(240,237,232,0.2)",
              marginTop: "8px",
              animation: "yen-showcase-fade 0.5s 0.12s ease both",
            }}
          >
            {current.type}
          </p>
        )}
      </div>

      {/* listen link */}
      <Link
        to={`/release/${current.slug}`}
        style={{
          fontFamily: F, fontSize: "9px", letterSpacing: "0.25em",
          textTransform: "uppercase", color: "rgba(240,237,232,0.45)",
          textDecoration: "none",
          border: "1px solid rgba(240,237,232,0.15)",
          padding: "8px 16px",
          transition: "all 0.15s",
          display: "inline-block",
          marginBottom: "32px",
        }}
        onMouseOver={e => { e.currentTarget.style.color = "#f0ede8"; e.currentTarget.style.borderColor = "rgba(240,237,232,0.5)"; }}
        onMouseOut={e => { e.currentTarget.style.color = "rgba(240,237,232,0.45)"; e.currentTarget.style.borderColor = "rgba(240,237,232,0.15)"; }}
      >
        Listen →
      </Link>

      <style>{`
        @keyframes yen-showcase-fade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
